import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { GanttAxisStart, GanttComponentData, GanttContext, GanttSortMode, GanttViewMode, GANTT_OPTION_DEFAULT } from "@core/data/types/advanced-report";
import { Snackbar } from "@core/material";
import { Select, Store } from "@ngxs/store";
import { Observable, ReplaySubject, Subject } from "rxjs";
import { InitReportGanttForm, ReportGanttFormState, ReportGanttFormStateModel } from "./report-gantt-edit-form.state";
import { Characteristic } from "@library";
import { SearchGroup, Status } from "@core/data";
import { takeUntil } from "rxjs/operators";

export interface ReportGanttEditFormDialogData {
    reportKey:   string;
    ownerOrgKey: string;
    optionKey:   string;
    data:        GanttComponentData;
}

@Component({
    selector: 'pv-report-gantt-edit-form-dialog',
    templateUrl: 'report-gantt-edit-form.dialog.html',
    styleUrls:   ['report-gantt-edit-form.dialog.scss'],
    preserveWhitespaces: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'pv-report-gantt-edit-form-dialog' }
})
export class ReportGanttEditFormDialog implements OnInit, OnDestroy {

    @Select(ReportGanttFormState)
    state$: Observable<ReportGanttFormStateModel>;

    @Select(ReportGanttFormState.dateChars)
    dateChars$: Observable<SearchGroup<Characteristic>[]>;

    readonly Status = Status;

    charFilterCtrl: FormControl = new FormControl();
    filteredCharGroups: ReplaySubject<SearchGroup<Characteristic>[]> = new ReplaySubject(1);
    private _allGroups: SearchGroup<Characteristic>[] = [];

    charIdsControl   = new FormControl([], [Validators.required, Validators.minLength(1)]);
    sortModeControl  = new FormControl('earliest', [Validators.required]);
    contextControl   = new FormControl('CULTIVAR',  [Validators.required]);
    titleControl     = new FormControl('', [Validators.maxLength(80)]);
    viewModeControl  = new FormControl('zoomed', [Validators.required]);
    axisStartControl = new FormControl(1, [Validators.required]);

    formGroup: FormGroup = new FormGroup({
        charIds:       this.charIdsControl,
        scale:         new FormControl('week'),
        sortMode:      this.sortModeControl,
        context:       this.contextControl,
        viewMode:      this.viewModeControl,
        axisStartWeek: this.axisStartControl,
        title:         this.titleControl,
    });

    readonly viewOptions: { value: GanttViewMode; label: string; icon: string }[] = [
        { value: 'zoomed', label: 'Zoomed',    icon: 'center_focus_strong' },
        { value: 'full',   label: 'Full year', icon: 'calendar_view_month' },
    ];

    readonly axisStartOptions: { value: GanttAxisStart; label: string; description: string; icon: string }[] = [
        { value: 1,  label: 'Calendar year', description: 'W01 → W53',  icon: 'calendar_today' },
        { value: 27, label: 'Split year',    description: 'W27 → W26 (wraps over the new year)', icon: 'sync_alt' },
    ];

    readonly sortOptions: { value: GanttSortMode; label: string; icon: string }[] = [
        { value: 'earliest', label: 'Earliest First', icon: 'arrow_upward' },
        { value: 'latest',   label: 'Latest First',   icon: 'arrow_downward' },
    ];

    readonly contextOptions: { value: GanttContext; label: string; description: string; icon: string }[] = [
        { value: 'CULTIVAR',   label: 'Cultivar',   description: 'One row per variety',         icon: 'yard' },
        { value: 'SITE',       label: 'Site',       description: 'One row per site',            icon: 'place' },
        { value: 'SAMPLE',     label: 'Sample',     description: 'One row per plant sample',    icon: 'eco' },
        { value: 'EVALUATION', label: 'Evaluation', description: 'One row per evaluation set',  icon: 'assignment' },
    ];

    private _destroy$ = new Subject<void>();

    constructor(
        private _dialogRef: MatDialogRef<ReportGanttEditFormDialog>,
        @Inject(MAT_DIALOG_DATA) private _data: ReportGanttEditFormDialogData,
        private _store: Store,
        private _snackbar: Snackbar,
    ) {}

    ngOnInit() {
        this._store.dispatch(new InitReportGanttForm(
            this._data.reportKey,
            this._data.ownerOrgKey,
            this._data.optionKey,
            this._data.data,
        ));

        // Merge over defaults so components saved before viewMode/title existed
        // still populate the new controls (back-compat, no migration).
        const current = { ...GANTT_OPTION_DEFAULT, ...(this._data.data ?? {}) };
        this.formGroup.patchValue(current, { emitEvent: false });

        this.dateChars$.pipe(takeUntil(this._destroy$)).subscribe(groups => {
            this._allGroups = groups || [];
            this._applyFilter(this.charFilterCtrl.value);
        });

        this.charFilterCtrl.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe(search => this._applyFilter(search));
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    attempt() {
        this.formGroup.markAllAsTouched();
        const charIds: string[] = this.charIdsControl.value ?? [];

        if (charIds.length < 1 || charIds.length > 2) {
            this._snackbar.error('Select 1 or 2 date characteristics for the Gantt chart.');
            return;
        }

        if (this.formGroup.valid) {
            this._dialogRef.close({ status: true, data: this.formGroup.value as GanttComponentData });
        } else {
            this._snackbar.formInvalid();
        }
    }

    cancel() {
        this._dialogRef.close({ status: false });
    }

    private _applyFilter(search: string) {
        if (!search || !search.trim()) {
            this.filteredCharGroups.next(this._allGroups);
            return;
        }
        const lower = search.trim().toLowerCase();
        const filtered = this._allGroups
            .map(g => ({ ...g, data: (g.data as Characteristic[]).filter(c => c.label.toLowerCase().includes(lower)) }))
            .filter(g => g.data.length > 0);
        this.filteredCharGroups.next(filtered);
    }
}
